import { twMerge } from 'tailwind-merge';
import { useState, MouseEvent } from 'react';
import { Menu, MenuItem } from '@mui/material';

import { Config } from 'config';
import { useIsDesktopDown } from 'hooks';
import { Icon, Button } from 'components';
import { getLangNameFromCode } from 'utils';
import { useTranslationContext } from 'context';

import { NavigationLink } from './navigation-link';

export const LanguageButton = ({
  className,
  buttonClassName,
}: {
  className?: string;
  buttonClassName?: string;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { language, languages, changeLanguage } = useTranslationContext();
  const isDesktopDown = useIsDesktopDown();
  if (languages.length < 2 || Config.supportedLanguages.length < 2) {
    return null;
  }

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const renderDesktopMenu = () => {
    return (
      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            className: 'p-0 border border-gray-primary mt-1',
          },
        }}
      >
        {languages.map(({ code, name }) => (
          <MenuItem
            key={code}
            value={code}
            onClick={() => {
              changeLanguage(code);
              handleClose();
            }}
          >
            {name}
          </MenuItem>
        ))}
      </Menu>
    );
  };

  const renderMobileMenu = () => {
    return (
      !!anchorEl && (
        <div>
          {languages.map(({ code, name }) => (
            <NavigationLink
              asButton
              key={code}
              label={name}
              variant="mobile"
              onClick={() => {
                changeLanguage(code);
                handleClose();
              }}
              className={twMerge(
                'w-full pl-12!',
                language === code && 'opacity-60'
              )}
            />
          ))}
        </div>
      )
    );
  };

  return (
    <div className={className}>
      <Button
        variant="text"
        className="font-bold"
        onClick={(event) => {
          handleOpen(event);
          if (isDesktopDown && !!anchorEl) {
            handleClose();
          }
        }}
        endIcon={
          <Icon.ChevronDown
            className={twMerge(
              '[&_path]:fill-white-primary',
              !!anchorEl && 'rotate-180'
            )}
          />
        }
        buttonClassName={twMerge(
          'min-w-0 p-0 hover:text-white-hover text-white-primary active:bg-light-gradient-text active:bg-clip-text active:text-transparent disabled:opacity-60',
          buttonClassName
        )}
      >
        {getLangNameFromCode(language)?.native}
      </Button>
      {isDesktopDown ? renderMobileMenu() : renderDesktopMenu()}
    </div>
  );
};
